import React, { useState } from 'react'

import {
  Box,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import { useForm, Controller } from 'react-hook-form'

import { BaseDialog, LoadingFeedback } from 'components'

import * as service from 'service'
import schema from './schema'
import theme from 'theme'

import useFetch from 'hooks/useFetch'
import useSnackbar from 'hooks/useSnackbar'

const AddCategoryDialog = ({ open, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false)
  const { control, handleSubmit, reset, errors } = useForm({
    validationSchema: schema,
    defaultValues: {
      name: '',
      responsiblesIds: [],
      customizable: false,
      deadlineDay: '',
      companyDeadlineDay: '',
    },
  })

  const { response: usersResponse, isLoading: loadingUsers } = useFetch(
    service.dponet.users.get,
    { perPage: 1000, minimal: true },
    [open],
    open,
  )
  const snackbar = useSnackbar()

  const users = usersResponse?.data?.users || []

  const handleSave = async (data) => {
    setLoading(true)
    try {
      await service.dponet.complaintCategories.create({
        complaintCategory: {
          name: data.name.trim(),
          responsiblesIds: data.responsiblesIds.map((user) => user.id),
          customizable: data.customizable,
          deadlineDay:
            data.deadlineDay === '' ? null : Number(data.deadlineDay),
          companyDeadlineDay:
            data.companyDeadlineDay === ''
              ? null
              : Number(data.companyDeadlineDay),
        },
      })
      snackbar.open({
        message: 'Categoria criada com sucesso!',
        variant: 'success',
      })
      handleClose()
      if (onSuccess) onSuccess()
    } catch (error) {
      console.error('Erro ao criar categoria:', error)
      snackbar.open({
        message: 'Erro ao criar categoria',
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    onClose()
    reset()
  }

  return (
    <BaseDialog
      dialogSize="md"
      fullWidth
      open={open}
      setOpen={handleClose}
      title="Nova Categoria de Denúncia"
      actionButton={handleSubmit(handleSave)}
      actionButtonText="Adicionar"
      closeButtonText="Cancelar"
      disableButton={loading || loadingUsers}
      loading={loading || loadingUsers}
    >
      <LoadingFeedback open={loading || loadingUsers} />
      <Box pt={1} pb={2}>
        <Controller
          name="name"
          control={control}
          as={
            <TextField
              fullWidth
              label="Nome da Categoria"
              variant="outlined"
              required
              error={!!errors.name}
              helperText={errors?.name?.message}
            />
          }
        />
      </Box>
      <Box pb={1}>
        <Controller
          name="responsiblesIds"
          control={control}
          onChange={([, data]) => data}
          as={
            <Autocomplete
              multiple
              fullWidth
              loading={loadingUsers}
              options={users}
              getOptionLabel={(option) => option.name || ''}
              getOptionSelected={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Responsáveis"
                  variant="outlined"
                  placeholder="Selecione os responsáveis"
                />
              )}
            />
          }
        />
      </Box>
      <Box pb={1} display="flex" gridGap={theme.spacing(2)}>
        <Controller
          name="deadlineDay"
          control={control}
          as={
            <TextField
              fullWidth
              required
              type="number"
              label="Prazo para finalização (dias)"
              variant="outlined"
              error={!!errors.deadlineDay}
              helperText={errors?.deadlineDay?.message}
            />
          }
        />
        <Controller
          name="companyDeadlineDay"
          control={control}
          as={
            <TextField
              fullWidth
              required
              type="number"
              label="Prazo da empresa (dias)"
              variant="outlined"
              error={!!errors.companyDeadlineDay}
              helperText={errors?.companyDeadlineDay?.message}
            />
          }
        />
      </Box>
      <Box pb={1}>
        <Controller
          name="customizable"
          control={control}
          onChange={([event]) => event.target.checked}
          as={
            <FormControlLabel
              label="Categoria customizável"
              labelPlacement="end"
              control={<Switch color="primary" />}
            />
          }
        />
      </Box>

      <Box
        p={2}
        bgcolor={theme.palette.custom.light}
        borderRadius={1}
        display="flex"
        flexDirection="column"
        gridGap={theme.spacing(1.5)}
      >
        <Typography color="primary" variant="h6">
          Informação
        </Typography>
        <Typography align="justify">
          Ao ativar a opção de <strong>"Categoria customizável"</strong>, o
          usuário poderá digitar livremente, caso contrário, você deverá
          adicionar as opções para a categoria.
        </Typography>
      </Box>
    </BaseDialog>
  )
}

export default AddCategoryDialog
